import { Modal } from 'antd';
import { Turnstile } from '@marsidev/react-turnstile';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useRouter } from 'next/router';

export type TurnstileVerifyResult = {
  success: boolean;
  token: string | null;
};

export type TurnstileModalHandle = {
  open: () => Promise<TurnstileVerifyResult>;
};

type TurnstileModalProps = {
  title?: string;
  desc?: string;
  siteKey?: string;
};

const TurnstileModal = forwardRef<TurnstileModalHandle, TurnstileModalProps>(
  (
    {
      title = 'Before we continue…',
      desc = 'Please complete the step below to continue.',
      siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '',
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const resolveRef = useRef<((result: TurnstileVerifyResult) => void) | null>(null);
    const promiseRef = useRef<Promise<TurnstileVerifyResult> | null>(null);
    const tokenRef = useRef<string | null>(null);
    const [mounted, setMounted] = useState(false);
    const router = useRouter();

    useEffect(() => {
      setMounted(true);
      return () => {
        if (resolveRef.current) {
          resolveRef.current({ success: false, token: null });
        }
      };
    }, []);

    const finish = useCallback((result: TurnstileVerifyResult) => {
      resolveRef.current?.(result);
      resolveRef.current = null;
      promiseRef.current = null;
      tokenRef.current = null;
      setIsLoading(false);
      setOpen(false);
    }, []);

    const openModal = useCallback(() => {
      if (typeof window === 'undefined') {
        return Promise.resolve({ success: false, token: null });
      }
      if (promiseRef.current) {
        return promiseRef.current;
      }
      tokenRef.current = null;
      setIsLoading(true);
      setOpen(true);
      promiseRef.current = new Promise<TurnstileVerifyResult>((resolve) => {
        resolveRef.current = resolve;
      });
      return promiseRef.current;
    }, []);

    useImperativeHandle(ref, () => ({
      open: openModal,
    }));

    return (
      <Modal
        open={open}
        onCancel={() => finish({ success: false, token: null })}
        footer={null}
        destroyOnHidden
        centered
        width={'auto'}
        closable={false}
        classNames={{
          content: 'rounded-2xl bg-white p-0',
          body: 'p-0',
        }}
        styles={{
          wrapper: {
            padding: 0,
          },
        }}
      >
        <div className="relative flex min-w-[358px] w-[calc(100vw-64px)] max-w-[424px] flex-col box-border p-6 sm:p-8">
          {isLoading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white">
              <div className="h-20 w-20 animate-spin rounded-full border-2 border-[#E5E7EB] border-t-[#725CFF]" />
            </div>
          ) : null}
          <div className={isLoading ? 'opacity-0' : 'opacity-100'}>
            <div className="text-[18px] sm:text-[20px] font-bold leading-[1.21] text-[#0C0C0C]">{title}</div>
            {desc ? <div className="mt-2 text-[14px] sm:text-[16px] leading-[1.21] text-[#0C0C0C]">{desc}</div> : null}
            <div className="mt-5 flex min-h-[80px] w-full items-center justify-center overflow-hidden">
              {mounted && open ? (
                <Turnstile
                  siteKey={siteKey}
                  options={{ theme: 'light', language: router.locale, size: 'flexible' }}
                  scriptOptions={{ appendTo: 'body' }}
                  as="aside"
                  onWidgetLoad={() => setIsLoading(false)}
                  onSuccess={(token) => {
                    tokenRef.current = token;
                    finish({ success: true, token });
                  }}
                  onError={() => finish({ success: false, token: null })}
                  onExpire={() => finish({ success: false, token: null })}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Modal>
    );
  }
);

TurnstileModal.displayName = 'TurnstileModal';

export default TurnstileModal;
